import { apiGateway } from "@/_old/services/api-gateway";
import type { AgentType, Conversation, Message } from "../types";
import { parseBlocks } from "../schemas/blocks";
import { resolveActionFallback } from "../utils/chatbotUtils";

type SenderType = "user" | "agent";

type ApiConversation = {
  id: number;
  userId: number;
  companyId: number;
  title: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /** Some backends already return this field; map it when present. */
  agentType?: string | null;
};

const AGENT_TYPE_MAP: Record<string, AgentType> = {
  operation: "operation",
  strategy: "estrategy",
  estrategy: "estrategy",
  experience: "experience",
};

function normalizeConversationAgentType(raw?: string | null): AgentType | null {
  if (!raw) return null;
  return AGENT_TYPE_MAP[raw.toLowerCase()] ?? null;
}

function normalizeConversationTitle(title: string): string {
  const normalizedTitle = title.trim();

  if (/\[[A-Z_]+_REDACTED\]/i.test(normalizedTitle)) {
    return "Título indisponível";
  }

  return normalizedTitle || "Nova conversa";
}

type ApiConversationMessage = {
  id: number;
  conversationId: number;
  content: string;
  type: string;
  actionType: string | null;
  senderType: SenderType;
  agentType?: string | null;
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
};

type ListConversationsResponse = {
  data?: ApiConversation[];
  conversations: ApiConversation[];
};

type ConversationDetailResponse = ApiConversation & {
  messages: ApiConversationMessage[];
};

function mapConversation(conversation: ApiConversation): Conversation {
  return {
    id: String(conversation.id),
    title: normalizeConversationTitle(conversation.title),
    pinned: conversation.pinned,
    created_at: conversation.createdAt,
    updated_at: conversation.updatedAt,
    agent_type: normalizeConversationAgentType(conversation.agentType),
  };
}

/**
 * Extrai os structured blocks persistidos no `metadata` da mensagem (ST-9), para
 * re-renderizar tabelas/gráficos/cards ao reabrir uma conversa do histórico.
 * O `metadata` pode vir como objeto (API já parseou) ou string JSON (coluna do banco);
 * blocks inválidos/desconhecidos são descartados pelo `parseBlocks` (RFC §2.4).
 */
function blocksFromMetadata(metadata: unknown): Message["blocks"] {
  if (!metadata) return null;
  let meta: unknown = metadata;
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      return null;
    }
  }
  if (typeof meta !== "object" || meta === null) return null;
  const raw = (meta as { blocks?: unknown }).blocks;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const blocks = parseBlocks(raw);
  return blocks.length ? blocks : null;
}

function mapMessage(message: ApiConversationMessage): Message {
  const isAgent = message.senderType === "agent";
  const isUser = message.senderType === "user";
  return {
    id: String(message.id),
    // User messages whose content is a raw action key (e.g. "action_do_for_me_cost_center")
    // are resolved to their human-readable fallback text so history bubbles never show the
    // internal identifier. Agent messages are passed through unchanged.
    content: isUser
      ? (resolveActionFallback(message.content) ?? message.content)
      : message.content,
    isUser,
    timestamp: new Date(message.createdAt),
    agent_type: isAgent
      ? normalizeConversationAgentType(message.agentType)
      : null,
    // Re-renderiza os blocks persistidos (só mensagens do agente têm metadata útil).
    blocks: isAgent ? blocksFromMetadata(message.metadata) : null,
  };
}

export async function listConversations({
  companyId,
}: {
  companyId: string | number;
}): Promise<Conversation[]> {
  const { data } = await apiGateway.get<ListConversationsResponse>(
    `/companies/${companyId}/ai-conversations`,
  );

  const conversations = data?.conversations ?? data?.data ?? [];

  return conversations.map(mapConversation);
}

export async function getConversationMessages({
  companyId,
  conversationId,
}: {
  companyId: string | number;
  conversationId: string | number;
}): Promise<Message[]> {
  const { data } = await apiGateway.get<ConversationDetailResponse>(
    `/companies/${companyId}/ai-conversations/${conversationId}`,
  );

  return (data.messages ?? [])
    .map(mapMessage)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

export async function deleteConversation({
  companyId,
  conversationId,
}: {
  companyId: string | number;
  conversationId: string | number;
}): Promise<void> {
  await apiGateway.delete(
    `/companies/${companyId}/ai-conversations/${conversationId}`,
  );
}
